import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { ApiSession } from "@fmc/contracts";
import { getSession } from "./api";
import { authClient } from "./auth-client";
import { clearAdminTenant, selectAdminTenant } from "./tenant-context";
import {
  clearSessionActive,
  markSessionActive,
  SESSION_EXPIRED_EVENT,
  type SessionExpiredDetail,
  type SessionExpiryTarget,
} from "./session-expiry";

export interface Session {
  loading: boolean;
  authenticated: boolean;
  impersonating: boolean;
  adminTenantAccess: boolean;
  brand: string;
  tenantKey: string;
  adminName: string;
  canEditConfig: boolean;
  canEditLeads: boolean;
  userName: string;
  role: ApiSession["tenant"]["role"] | null;
  isFmcAdmin: boolean;
  fmcAdminRole: ApiSession["fmcAdminRole"];
  expiryTarget: SessionExpiryTarget | null;
  refresh: () => Promise<boolean>;
  enterAdminTenant: (tenantId: string) => Promise<boolean>;
  exitClientView: () => Promise<void>;
  exit: () => void;
}

const defaults: Session = {
  loading: true,
  authenticated: false,
  impersonating: false,
  adminTenantAccess: false,
  brand: "",
  tenantKey: "",
  adminName: "",
  canEditConfig: false,
  canEditLeads: false,
  userName: "",
  role: null,
  isFmcAdmin: false,
  fmcAdminRole: null,
  expiryTarget: null,
  refresh: async () => false,
  enterAdminTenant: async () => false,
  exitClientView: async () => {},
  exit: () => {},
};

const SessionContext = createContext<Session>(defaults);
export function useSession() {
  return useContext(SessionContext);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<ApiSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [expiryTarget, setExpiryTarget] = useState<SessionExpiryTarget | null>(null);
  const refresh = async () => {
    setLoading(true);
    try {
      const next = await getSession();
      markSessionActive();
      setExpiryTarget(null);
      setData(next);
      return true;
    } catch {
      setData(null);
      return false;
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      const { target } = (event as CustomEvent<SessionExpiredDetail>).detail;
      clearAdminTenant();
      setExpiryTarget(target);
      setData(null);
      setLoading(false);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    void refresh();
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  const enterAdminTenant = async (tenantId: string) => {
    setLoading(true);
    selectAdminTenant(tenantId);
    try {
      const next = await getSession();
      markSessionActive();
      setExpiryTarget(null);
      setData(next);
      return true;
    } catch {
      clearAdminTenant();
      try {
        const next = await getSession();
        markSessionActive();
        setExpiryTarget(null);
        setData(next);
      } catch {
        setData(null);
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const exitClientView = async () => {
    clearAdminTenant();
    await refresh();
  };

  const value = useMemo<Session>(() => {
    if (!data) return { ...defaults, loading, expiryTarget, refresh };
    return {
      loading,
      authenticated: true,
      impersonating: data.adminTenantAccess,
      adminTenantAccess: data.adminTenantAccess,
      brand: data.tenant.name,
      tenantKey: data.tenant.id,
      adminName: data.user.name,
      canEditConfig: ["owner", "manager"].includes(data.tenant.role),
      canEditLeads: ["owner", "manager", "analyst"].includes(data.tenant.role),
      userName: data.user.name,
      role: data.tenant.role,
      isFmcAdmin: data.isFmcAdmin,
      fmcAdminRole: data.fmcAdminRole,
      expiryTarget,
      refresh,
      enterAdminTenant,
      exitClientView,
      exit: () => {
        clearAdminTenant();
        clearSessionActive();
        void authClient.signOut().finally(() => {
          setData(null);
          window.location.assign("/login");
        });
      },
    };
  }, [data, expiryTarget, loading]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
